<template>
  <div @click.stop.prevent="$emit('go', point)" :class="['item btn-default', { active: active }]">
    <AppAvatar :src="avatarSrc" class="avatar__auto-fill mr-2" :circle="avatarHasCircle" />
    <div class="description">
      <div class="title" v-html="title"></div>
      <div class="note"><i class="lnr lnr-apartment mr-1"></i>{{ note }}</div>
    </div>
  </div>
</template>

<script>
import escape from 'lodash/escape'
import { mapGetters } from 'vuex'
import AppAvatar from '@/components/shared/AppAvatar'
import { getAvatarSrc } from '@/utils/Helpers'
import { getIconUrl } from '@/components/leaflet/Icon'
import { POINT_ICONS } from '@/store/modules/point'

export default {
  name: 'AppNavSearchItem',
  components: { AppAvatar },
  props: {
    point: {
      required: true,
      type: Object
    },
    active: {
      type: Boolean,
      default: false
    }
  },
  computed: {
    ...mapGetters('building', ['getBuilding']),
    ...mapGetters('floor', ['getFloor']),
    ...mapGetters('pointIcon', ['getPointIcon']),
    ...mapGetters('position', ['getPosition']),
    ...mapGetters('department', ['getDepartment']),
    floor() {
      return this.getFloor(this.point.floor_id)
    },
    building() {
      if (this.floor) {
        return this.getBuilding(this.floor.building_id)
      }
      return null
    },
    position() {
      if (this.point.employee && this.point.employee.position_id) {
        return this.getPosition(this.point.employee.position_id)
      }
      return ''
    },
    department() {
      if (this.point.employee && this.point.employee.department_id) {
        return this.getDepartment(this.point.employee.department_id)
      }

      if (this.point.printer && this.point.printer.department_id) {
        return this.getDepartment(this.point.printer.department_id)
      }
      return ''
    },
    title() {
      if (this.point.employee) {
        let title = `<b>${escape(this.point.employee.name)}</b>`
        if (this.position) title += `<br>${escape(this.position.name)}`
        if (this.department) title += `<br>${escape(this.department.name)}`
        if (this.point.employee.hrid) title += `<br>Employee ID: ${escape(this.point.employee.hrid)}`
        if (this.point.desk.number) title += `<br>Desk No: ${escape(this.point.desk.number)}`
        return title
      }

      if (this.point.room) {
        return `<b>Room ${escape(this.point.room.name)}</b>`
      }

      if (this.point.printer) {
        let title = `<b>Printer ${escape(this.point.printer.name)}</b>`
        if (this.point.printer.model) title += `<br>${escape(this.point.printer.model)}`
        if (this.department) title += `<br>${escape(this.department.name)}`
        return title
      }

      if (this.point.desk) {
        return `<b>Desk ${escape(this.point.desk.number || '')}</b>`
      }

      if (this.point.pc) {
        let title = `<b>PC ${escape(this.point.pc.name)}</b>`
        if (this.point.pc.model) title += `<br>${escape(this.point.pc.model)}`
        if (this.department) title += `<br>${escape(this.department.name)}`
        return title
      }

      const pointTitle = `<b>${escape(this.pointIcon?.name ?? '')}</b><br>${escape(this.point?.title ?? '')}`
      return pointTitle
    },
    note() {
      return `${this.building ? `${this.building.name}, ` : ''} ${this.floor ? this.floor.name : ''}`
    },
    pointIcon() {
      return this.getPointIcon(this.point.icon_id)
    },
    iconSrc() {
      return getIconUrl(this.point.icon_id, this.point.file_updated_at)
    },
    avatarHasCircle() {
      return this.point.icon_id !== POINT_ICONS.AREA
    },
    avatarSrc() {
      if (this.point.employee) {
        const employee = this.point.employee
        return getAvatarSrc(employee.avatar_id, employee.avatar_updated_at, this.iconSrc)
      }

      if (this.point.room) {
        const room = this.point.room
        return getAvatarSrc(room.avatar_id, room.avatar_updated_at, this.iconSrc)
      }

      if (this.point.printer) {
        const printer = this.point.printer
        return getAvatarSrc(printer.avatar_id, printer.avatar_updated_at, this.iconSrc)
      }

      if (this.point.icon_id === POINT_ICONS.AREA) {
        return '/img/area-icon.png'
      }

      return this.iconSrc
    }
  }
}
</script>

<style scoped lang="scss">
.item {
  padding: 0.6rem 1rem;
  display: flex;
  align-items: center;
  cursor: pointer;
}

.user-avatar {
  margin-right: 0.6rem;
  width: 35px;
  height: 35px;
  background-size: contain !important;
}
</style>
